import {WalletPlatform} from '@/enums/WalletPlatform'
import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'
import {
  RejectedByUser,
  SdkNotInitializedError,
} from '@/model/wallets/types/WalletErrors'
import {
  registerNep21ContextListeners,
  NeolineContextChangeHandler,
} from './NeolineContextListeners'
import {validateNeoLineAddressNetwork} from './NeolineNetworkValidation'
import {ConnectionStrategy} from './ConnectionStrategy'
import {nep21Registry} from './NEP21Registry'
import {
  isNeolineExtensionProvider,
  NeolineExtensionConnection,
  NeolineExtensionProviderContext,
} from './NeolineExtensionConnection'
import {
  isNeolineNeoXEthereumProvider,
  isNeoLineEthereumProviderAvailable,
  NeolineNeoXEthereumConnection,
  NeolineNeoXEthereumProviderContext,
} from './NeolineNeoXEthereumConnection'

export type NeoLineNep21ProviderContext = {
  type: 'neoline-nep21'
  provider: any
  address: string | null
}

export type NeoLineProviderContext =
  | NeolineExtensionProviderContext
  | NeoLineNep21ProviderContext
  | NeolineNeoXEthereumProviderContext

export const isNeoLineNep21Provider = (
  provider: unknown
): provider is NeoLineNep21ProviderContext =>
  typeof provider === 'object' &&
  provider !== null &&
  (provider as NeoLineNep21ProviderContext).type === 'neoline-nep21'

export const isNeoLineProvider = (
  provider: unknown
): provider is NeoLineProviderContext =>
  isNeolineExtensionProvider(provider) ||
  isNeoLineNep21Provider(provider) ||
  isNeolineNeoXEthereumProvider(provider)

const getInjectedNeoLineN3Provider = (): any => {
  if (typeof window === 'undefined') return null

  return (window as any).NeoLineN3Provider ?? null
}

export const isNeoLineInAppBrowser = (): boolean =>
  getInjectedNeoLineN3Provider() !== null
const resolveNeoLineNep21Provider = (): any =>
  nep21Registry.get('NeoLine') ?? getInjectedNeoLineN3Provider()

export const isNeoLineAvailableInBrowser = (
  implementation: 'n3' | 'neoX' = 'n3'
) => {
  if (typeof window === 'undefined') return false

  if (implementation === 'neoX') {
    if (isNeoLineEthereumProviderAvailable()) return true

    return typeof (window as any).NEOLineNEOX !== 'undefined'
  }

  if (typeof (window as any).NEOLineN3 !== 'undefined') return true

  nep21Registry.init()

  return !!resolveNeoLineNep21Provider()
}

type NeoLineConnectionMode = 'extension' | 'nep21' | 'ethereum'

export class NeoLineConnection implements ConnectionStrategy {
  private readonly extension: NeolineExtensionConnection
  private readonly neoXEthereum: NeolineNeoXEthereumConnection | null
  private mode: NeoLineConnectionMode | null = null
  private nep21Provider: any = null
  private nep21Address: string | null = null
  private detachContextListeners?: () => void

  constructor(
    private readonly implementation: 'n3' | 'neoX',
    private readonly network: TWalletNetwork
  ) {
    this.extension = new NeolineExtensionConnection(implementation, network)

    this.neoXEthereum =
      implementation === 'neoX'
        ? new NeolineNeoXEthereumConnection(network)
        : null
  }

  async init(onDeleteAdapter?: () => void) {
    this.detachContextListeners?.()

    nep21Registry.init()

    const onContextChange = this.createContextChangeHandler(onDeleteAdapter)

    if (this.implementation === 'neoX') {
      if (await this.tryInitNeoXEthereum(onContextChange)) return

      if (await this.tryInitExtension(onContextChange)) return
    } else {
      if (await this.tryInitExtension(onContextChange)) return

      if (await this.tryInitNep21(onContextChange)) return
    }

    throw SdkNotInitializedError(WalletPlatform.NEOLINE)
  }

  async restoreSession() {
    if (this.mode === 'nep21' && this.nep21Provider) {
      await this.restoreNep21Address()
      return
    }

    if (this.mode === 'extension') {
      await this.extension.restoreSession()
      return
    }

    if (this.mode === 'ethereum' && this.neoXEthereum) {
      await this.neoXEthereum.restoreSession?.()
    }
  }

  restoreBlockedByNetwork() {
    if (this.mode === 'ethereum' && this.neoXEthereum) {
      return this.neoXEthereum.restoreBlockedByNetwork()
    }

    if (this.mode === 'extension') {
      return this.extension.restoreBlockedByNetwork()
    }

    return false
  }

  isInstalled() {
    if (this.implementation === 'neoX') {
      return (
        this.neoXEthereum?.isInstalled() ||
        this.extension.isInstalled() ||
        false
      )
    }

    return (
      this.extension.isInstalled() ||
      !!resolveNeoLineNep21Provider() ||
      !!this.nep21Provider
    )
  }

  async connect() {
    this.ensureConnectionMode()

    if (this.mode === 'ethereum' && this.neoXEthereum) {
      return this.neoXEthereum.connect()
    }

    if (this.mode === 'nep21' && this.nep21Provider) {
      this.nep21Address = await this.requestNep21Connection(this.nep21Provider)

      if (!this.nep21Address) {
        throw RejectedByUser
      }

      validateNeoLineAddressNetwork(this.nep21Address, this.implementation)

      return {
        type: 'neoline-nep21',
        provider: this.nep21Provider,
        address: this.nep21Address,
      } as NeoLineNep21ProviderContext
    }

    if (this.mode === 'extension') {
      return this.extension.connect()
    }

    throw SdkNotInitializedError(WalletPlatform.NEOLINE)
  }

  async ensureNetwork(): Promise<void> {
    if (this.mode === 'ethereum' && this.neoXEthereum) {
      await this.neoXEthereum.ensureNetwork()
      return
    }

    if (this.mode === 'extension') {
      await this.extension.ensureNetwork()
    }
  }

  getConnectedProvider(): NeoLineProviderContext | null {
    if (this.mode === 'ethereum' && this.neoXEthereum) {
      return this.neoXEthereum.getConnectedProvider()
    }

    if (this.mode === 'extension') {
      return this.extension.getConnectedProvider()
    }

    if (this.mode === 'nep21' && this.nep21Provider) {
      return {
        type: 'neoline-nep21',
        provider: this.nep21Provider,
        address: this.nep21Address,
      }
    }

    return null
  }

  async disconnect() {
    this.detachContextListeners?.()

    this.detachContextListeners = undefined

    if (this.mode === 'ethereum' && this.neoXEthereum) {
      await this.neoXEthereum.disconnect()
      return
    }

    if (this.mode === 'extension') {
      await this.extension.disconnect()
      return
    }

    if (this.mode === 'nep21') {
      this.clearNep21CachedAccount(this.nep21Provider)

      this.nep21Address = null
    }
  }

  private createContextChangeHandler(
    onDeleteAdapter?: () => void
  ): NeolineContextChangeHandler {
    return () => {
      void this.handleContextChange(onDeleteAdapter)
    }
  }

  private async handleContextChange(onDeleteAdapter?: () => void) {
    await this.restoreSession()

    if (!this.getActiveAddress()) {
      this.nep21Address = null
      this.clearNep21CachedAccount(this.nep21Provider)
      onDeleteAdapter?.()
    }
  }

  private getActiveAddress(): string | null {
    if (this.mode === 'nep21') {
      return this.nep21Address
    }

    if (this.mode === 'extension') {
      return this.extension.getConnectedProvider()?.address ?? null
    }

    if (this.mode === 'ethereum' && this.neoXEthereum) {
      return this.neoXEthereum.getConnectedProvider()?.address ?? null
    }

    return null
  }

  private ensureConnectionMode() {
    nep21Registry.init()

    if (this.implementation === 'neoX') {
      if (
        isNeoLineEthereumProviderAvailable() ||
        this.neoXEthereum?.isInstalled()
      ) {
        this.mode = 'ethereum'

        return
      }

      if (this.mode === 'extension' && this.extension.isInstalled()) {
        return
      }

      if (this.extension.isInstalled()) {
        this.mode = 'extension'
      }

      return
    }

    if (this.extension.isInstalled()) {
      this.mode = 'extension'
      return
    }

    const nep21Provider = resolveNeoLineNep21Provider() ?? this.nep21Provider

    if (nep21Provider) {
      this.nep21Provider = nep21Provider
      this.mode = 'nep21'
      return
    }

    if (this.mode === 'extension' && this.extension.isInstalled()) return
    if (this.mode === 'nep21' && this.nep21Provider) return
  }

  private async tryInitNeoXEthereum(
    onContextChange: NeolineContextChangeHandler
  ): Promise<boolean> {
    if (!this.neoXEthereum) return false

    try {
      await this.neoXEthereum.init(onContextChange)

      this.mode = 'ethereum'

      return true
    } catch (error) {
      console.warn('[NeoLine] Neo X Ethereum provider init failed', error)

      return false
    }
  }

  private async tryInitExtension(
    onContextChange: NeolineContextChangeHandler
  ): Promise<boolean> {
    if (!this.extension.isInstalled()) return false

    try {
      await this.extension.init(onContextChange)

      this.mode = 'extension'

      return true
    } catch (error) {
      console.warn('[NeoLine] legacy extension init failed', error)

      return false
    }
  }

  private async tryInitNep21(
    onContextChange: NeolineContextChangeHandler
  ): Promise<boolean> {
    const provider =
      resolveNeoLineNep21Provider() ?? (await this.waitForNep21Provider(15000))

    if (!provider) return false

    this.nep21Provider = provider

    this.mode = 'nep21'

    this.detachContextListeners = registerNep21ContextListeners(
      this.nep21Provider,

      onContextChange
    )

    return true
  }

  private clearNep21CachedAccount(provider: any) {
    if (!provider) return

    if (provider.account) {
      provider.account = undefined
    }
  }

  private waitForNep21Provider(timeoutMs: number): Promise<any | null> {
    const existing = resolveNeoLineNep21Provider()

    if (existing) {
      return Promise.resolve(existing)
    }

    return new Promise(resolve => {
      const done = (provider: any | null) => {
        clearTimeout(timeoutId)

        clearInterval(pollId)

        window.removeEventListener('Neo.DapiProvider.ready', onReady)

        resolve(provider)
      }

      const timeoutId = setTimeout(() => done(null), timeoutMs)

      const onReady = (event: any) => {
        const provider = event?.detail?.provider

        if (provider?.name?.toLowerCase() === 'neoline') {
          done(nep21Registry.get('NeoLine') ?? provider)
        }
      }

      window.addEventListener('Neo.DapiProvider.ready', onReady)

      const pollId = setInterval(() => {
        const provider = resolveNeoLineNep21Provider()

        if (provider) {
          done(provider)
        }
      }, 200)
    })
  }

  private async restoreNep21Address() {
    if (!this.nep21Provider) {
      this.nep21Address = null
      return
    }

    this.requestNep21ProviderAnnouncement()

    try {
      if (typeof this.nep21Provider.getAccounts === 'function') {
        const accounts = await this.nep21Provider.getAccounts()
        const first = Array.isArray(accounts) ? accounts[0] : accounts
        const address = first?.address ?? null

        if (address) {
          validateNeoLineAddressNetwork(address, this.implementation)
          this.nep21Address = address
          return
        }
      }

      const address = await this.resolveNep21Address(this.nep21Provider)

      if (address) {
        validateNeoLineAddressNetwork(address, this.implementation)
      }

      this.nep21Address = address
    } catch {
      this.nep21Address = null
    }
  }

  private async resolveNep21Address(provider: any): Promise<string | null> {
    if (provider?.account?.address) {
      return provider.account.address
    }

    if (typeof provider.getAccount === 'function') {
      const account = await provider.getAccount()

      return account?.address ?? null
    }

    return null
  }

  private async requestNep21Connection(provider: any): Promise<string | null> {
    this.requestNep21ProviderAnnouncement()

    try {
      if (typeof provider.getAccount === 'function') {
        const account = await provider.getAccount()

        const address = account?.address ?? null

        if (address) {
          validateNeoLineAddressNetwork(address, this.implementation)
        }

        return address
      }

      if (typeof provider.getAccounts === 'function') {
        const accounts = await provider.getAccounts()

        const first = Array.isArray(accounts) ? accounts[0] : accounts

        const address = first?.address ?? null

        if (address) {
          validateNeoLineAddressNetwork(address, this.implementation)
        }

        return address
      }
    } catch (error) {
      if (
        error?.type === 'CONNECTION_DENIED' ||
        error?.code === 4001 ||
        error === RejectedByUser
      ) {
        throw RejectedByUser
      }

      throw error
    }

    return this.resolveNep21Address(provider)
  }

  private requestNep21ProviderAnnouncement() {
    if (typeof window === 'undefined') {
      return
    }

    window.dispatchEvent(
      new CustomEvent('Neo.DapiProvider.request', {
        detail: {type: 'NEP-21'},
      })
    )
  }
}
